import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layouts/MainLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import EmptyState from '../../components/common/EmptyState';
import SubscriptionStatusBadge from '../../components/subscription/SubscriptionStatusBadge';
import { useToast } from '../../components/common/Toast';
import { subscriptionService } from '../../services/subscription.service';
import { formatDate, formatCurrency } from '../../utils/helpers';
import { theme } from '../../config/theme';
import { ClockIcon } from '../../components/icons/Icons';

const TIER_LABELS = { free: 'Free', monthly: 'Monthly', yearly: 'Yearly' };

const SubscriptionHistory = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await subscriptionService.getHistory(20);
        setSubscriptions(res.data?.subscriptions || []);
      } catch {
        toast.error('Failed to load subscription history');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <LoadingSpinner fullScreen />;

  return (
    <MainLayout>
      <div style={{ padding: theme.spacing.xl, maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md, marginBottom: theme.spacing.xl }}>
          <Button variant="ghost" onClick={() => navigate('/seller/subscription')}>
            ← Back
          </Button>
          <div>
            <h1 style={{ fontSize: theme.typography.fontSize['3xl'], fontWeight: theme.typography.fontWeight.bold }}>
              Subscription History
            </h1>
            <p style={{ color: theme.colors.text.secondary, marginTop: theme.spacing.xs }}>
              {subscriptions.length} record{subscriptions.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {subscriptions.length === 0 ? (
          <EmptyState
            icon={<ClockIcon size={48} />}
            title="No subscription history"
            description="Your past subscriptions will appear here"
            action={<Button onClick={() => navigate('/seller/subscription')}>Browse Plans</Button>}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
            {subscriptions.map(sub => (
              <Card key={sub.id} padding="lg">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: theme.spacing.md }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                      <span style={{ fontWeight: theme.typography.fontWeight.semibold }}>
                        {TIER_LABELS[sub.tier] || sub.tier} Plan
                      </span>
                      <SubscriptionStatusBadge status={sub.status} />
                    </div>
                    <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                      {formatDate(sub.start_date)} — {sub.end_date ? formatDate(sub.end_date) : 'Ongoing'}
                    </span>
                    {sub.cancelled_at && (
                      <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>
                        Cancelled: {formatDate(sub.cancelled_at)}
                      </span>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: theme.typography.fontWeight.bold, fontSize: theme.typography.fontSize.lg }}>
                      {formatCurrency(sub.amount)}
                    </div>
                    <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary, textTransform: 'capitalize' }}>
                      {sub.payment_method}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default SubscriptionHistory;